import { TooltipModule } from "primeng/tooltip";
import {
    Component,
    Input,
    ViewChild,
    OnInit,
    SimpleChanges,
    OnChanges,
    AfterViewInit,
    Inject,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { ElementRef } from "@angular/core";
import { Observable, of, combineLatest, BehaviorSubject } from "rxjs";
import { OnboardingGuideComponent } from "@/components/shared/onboarding-guide/onboarding-guide.component";
import {
    isAsapStepList,
    OnboardingStepResponse,
} from "@/_models/onboarding-step-response.interface";
import { UserService, USER_IS_AT_LEAST_ADMIN } from "@/_services";
import { OnboardingGuideService } from "@/_services/onboarding-guide.service";
import { TerminologyService } from "@/_services/terminology/terminology.service";
import { SetupGuideService } from "@/_services/setup-guide.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { ResponsiveService } from "@/components/shared/responsive/responsive.service";
import {
    map,
    switchMap,
    filter,
    shareReplay,
    distinctUntilChanged,
} from "rxjs/operators";
import { OnboardingDismissalStorage } from "@/_services/storage";
import { DismissedBanner } from "@/_models";

@UntilDestroy()
@Component({
    selector: "app-setup-guide-button",
    standalone: true,
    imports: [CommonModule, OnboardingGuideComponent, TooltipModule],
    templateUrl: "./setup-guide-button.component.html",
    styleUrls: ["./setup-guide-button.component.scss"],
})
export class SetupGuideButtonComponent
    implements OnInit, OnChanges, AfterViewInit
{
    @Input() hasNotDismissedBanner: boolean = true;
    @Input() isCollapsedSidebarButton = false;
    @Input() asapFormId$?: Observable<string | null>;

    showButton$: Observable<boolean>;
    showGuide$: Observable<boolean>;
    private hasNotDismissedBanner$ = new BehaviorSubject<boolean>(true);

    @ViewChild("onboardingGuide")
    onboardingGuide?: OnboardingGuideComponent;

    @ViewChild("desktopButtonRef")
    desktopButtonRef?: ElementRef;
    @ViewChild("mobileButtonRef")
    mobileButtonRef?: ElementRef;

    steps$: Observable<OnboardingStepResponse[]> = of([]);
    guideTitle$: Observable<string> = of("");
    stepsArray: number[] = [];
    completedSteps: number = 0;
    maxSteps: number = 0;
    isMobile: boolean = false;
    currentAsapFormId: string | null = null;
    private hasTriggeredCompletionDismiss = false;
    private guideHasBeenOpened = false;

    constructor(
        private responsiveService: ResponsiveService,
        private onboardingGuideService: OnboardingGuideService,
        private setupGuideService: SetupGuideService,
        private userService: UserService,
        private terminology: TerminologyService,
        @Inject(USER_IS_AT_LEAST_ADMIN)
        private isAtLeastAdmin$: Observable<boolean>,
    ) {
        this.hasNotDismissedBanner$.next(this.hasNotDismissedBanner);
    }

    ngOnInit(): void {
        this.checkInitialScreenSize();
        this.initShowButtonLogic();
        this.initGuideListeners();
    }

    private initShowButtonLogic(): void {
        this.showButton$ = combineLatest([
            this.isAtLeastAdmin$,
            this.hasNotDismissedBanner$,
        ]).pipe(
            map(([isAdmin, hasNotDismissed]) => {
                if (!hasNotDismissed) {
                    return false;
                }

                return !!isAdmin;
            }),
            shareReplay(1),
        );

        this.showButton$
            .pipe(
                filter((showButton) => showButton),
                switchMap(() => {
                    const steps =
                        this.onboardingGuideService.getStepsSnapshot();
                    if (steps.length === 0) {
                        return this.onboardingGuideService.refresh(
                            this.currentAsapFormId || undefined,
                        );
                    }
                    return of(null);
                }),
                untilDestroyed(this),
            )
            .subscribe();
    }

    ngAfterViewInit(): void {
        this.initAutoOpenBehavior();
        this.setupGuideService.open$
            .pipe(untilDestroyed(this))
            .subscribe((event) => this.openGuide(event));
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes["hasNotDismissedBanner"]) {
            this.hasNotDismissedBanner$.next(this.hasNotDismissedBanner);
        }
        if (changes["asapFormId$"] && this.asapFormId$) {
            this.asapFormId$
                .pipe(untilDestroyed(this))
                .subscribe((asapFormId) => {
                    this.currentAsapFormId = asapFormId;
                    this.refreshSteps();
                });
        }
    }

    private initGuideListeners(): void {
        this.initResponsiveListener();
        this.initStepsStreams();
        this.initGuideTitleStream();
        this.initGuideStateSync();
    }

    private checkInitialScreenSize(): void {
        if (this.responsiveService.isMobile !== undefined) {
            this.isMobile = this.responsiveService.isMobile;
        }
    }

    private initResponsiveListener(): void {
        this.responsiveService.responsiveChangeSubject
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                this.isMobile = this.responsiveService.isMobile;
            });
    }

    private initStepsStreams(): void {
        this.steps$ = this.onboardingGuideService.steps$.pipe(shareReplay(1));

        this.showGuide$ = combineLatest([this.showButton$, this.steps$]).pipe(
            map(([showButton, steps]) => showButton && steps.length > 0),
            distinctUntilChanged(),
            shareReplay(1),
        );

        const asapFormId$ = this.asapFormId$ || of(null);

        asapFormId$
            .pipe(
                switchMap((asapFormId) => {
                    this.currentAsapFormId = asapFormId;
                    return this.onboardingGuideService.getOnboardingSteps(
                        asapFormId,
                    );
                }),
                untilDestroyed(this),
            )
            .subscribe();

        this.steps$.pipe(untilDestroyed(this)).subscribe((steps) => {
            this.maxSteps = steps.length;
            this.completedSteps = steps.filter((step) => step.completed).length;
            this.stepsArray = Array.from(
                { length: this.maxSteps },
                (_, i) => i + 1,
            );
        });

        combineLatest([this.steps$, this.showButton$])
            .pipe(untilDestroyed(this))
            .subscribe(([steps, showButton]) => {
                const allComplete =
                    showButton &&
                    steps.length > 0 &&
                    steps.every((s) => s.completed);

                if (allComplete) {
                    OnboardingDismissalStorage.remove();
                    if (!this.hasTriggeredCompletionDismiss) {
                        this.hasTriggeredCompletionDismiss = true;
                        this.userService
                            .dismissBanner(
                                DismissedBanner.OnboardingGuide,
                                true,
                            )
                            .subscribe({
                                next: () =>
                                    this.hasNotDismissedBanner$.next(false),
                                error: () => {
                                    this.hasTriggeredCompletionDismiss = false;
                                },
                            });
                    }
                } else {
                    this.hasTriggeredCompletionDismiss = false;
                }
            });
    }

    private initGuideTitleStream(): void {
        this.guideTitle$ = this.steps$.pipe(
            map((steps) =>
                this.terminology.resolve(
                    isAsapStepList(steps) ? "setup.title.asap" : "setup.title",
                ),
            ),
        );
    }

    private initGuideStateSync(): void {
        combineLatest([this.onboardingGuideService.guideState$, this.steps$])
            .pipe(
                filter(([state, steps]) => state.isOpen && steps.length > 0),
                distinctUntilChanged(
                    ([stateA], [stateB]) => stateA.isOpen === stateB.isOpen,
                ),
                untilDestroyed(this),
            )
            .subscribe(([state]) => {
                if (this.onboardingGuide) {
                    const syntheticEvent = this.createSyntheticEvent(
                        state.event,
                    );
                    if (syntheticEvent.target) {
                        this.guideHasBeenOpened = true;
                        this.onboardingGuide.toggle(syntheticEvent);
                    }
                }
            });
    }

    private rememberDismissalWhenOpenedGuideCloses(): void {
        this.onboardingGuideService.guideState$
            .pipe(untilDestroyed(this))
            .subscribe((state) => {
                if (this.guideHasBeenOpened && !state.isOpen) {
                    OnboardingDismissalStorage.put(true);
                }
            });
    }

    private initAutoOpenBehavior(): void {
        let hasAutoOpened = false;

        this.rememberDismissalWhenOpenedGuideCloses();

        combineLatest([this.steps$, this.showButton$])
            .pipe(
                filter(([steps, showButton]) => steps.length > 0 && showButton),
                untilDestroyed(this),
            )
            .subscribe(([steps]) => {
                const isDismissed = OnboardingDismissalStorage.get();

                if (hasAutoOpened || isDismissed) return;

                const hasIncompleteSteps = steps.some(
                    (step) => !step.completed,
                );
                if (
                    hasIncompleteSteps &&
                    this.onboardingGuide &&
                    !this.isMobile &&
                    !this.isCollapsedSidebarButton
                ) {
                    hasAutoOpened = true;
                    const syntheticEvent = this.createSyntheticEvent();
                    if (syntheticEvent.target) {
                        this.guideHasBeenOpened = true;
                        this.onboardingGuide.toggle(syntheticEvent);
                    }
                }
            });
    }

    private refreshSteps() {
        this.onboardingGuideService
            .refresh(this.currentAsapFormId || undefined)
            .subscribe();
    }

    openGuide(event: Event) {
        if (this.onboardingGuide) {
            this.guideHasBeenOpened = true;
            this.onboardingGuide.toggle(event);
        }
    }

    onStepsChange(steps: OnboardingStepResponse[]) {
        this.onboardingGuideService.setSteps(steps);
        this.refreshSteps();
    }

    private createSyntheticEvent(sourceEvent?: Event): Event {
        const target = this.isMobile
            ? this.mobileButtonRef?.nativeElement
            : this.desktopButtonRef?.nativeElement;
        return {
            target,
            currentTarget: target,
            preventDefault: () => sourceEvent?.preventDefault?.(),
            stopPropagation: () => sourceEvent?.stopPropagation?.(),
        } as unknown as Event;
    }
}
